"use client";
import Link from "next/link";
import { AspectRatio } from "../ui/aspect-ratio";
import { Button } from "../ui/button";
import { Reveal } from "../reveal";
import { ExtractBySectionType, ExtractPropertyType, SectionType } from "types";
import { PortableText } from "next-sanity";
import { iconsMap } from "../icons";
import { SanityImage } from "../sanity-image";

type ResourcesSectionType = ExtractBySectionType<
  SectionType,
  "resourcesSection",
  "_type"
>;

type ResourceType = ExtractPropertyType<
  ResourcesSectionType,
  "resources"
>[number];

function Resource({ resource }: { resource: ResourceType }) {
  const date = resource.eventDate || resource.publishedAt;
  const timeEstimate = resource.timeEstimate;
  return (
    <Reveal className="w-full flex flex-col gap-6">
      <AspectRatio ratio={350 / 250}>
        <SanityImage
          src={resource.image}
          placeholder="blur"
          blurDataURL={resource.image.asset?.metadata?.lqip || ""}
          alt="shipment tracker"
          fill
          className="object-cover rounded-2xl cursor-pointer"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </AspectRatio>
      <Button
        className="w-fit"
        variant={resource.callToAction.variant}
        radius="full"
      >
        {resource.callToAction.textBeforeIcon}
        {resource.callToAction.icon && iconsMap[resource.callToAction.icon]}
        {resource.callToAction.textAfterIcon}
      </Button>
      <div className="flex gap-3 flex-col">
        <h4 className="font-medium text-base lg:text-lg">{resource.title}</h4>
        {date && (
          <div className="flex flex-row gap-2 items-center">
            <p className="text-primary-300 text-xs lg:text-base">
              {new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              }).format(new Date(date))}
              {timeEstimate && <span> • {timeEstimate} min read</span>}
            </p>
            {resource.platform && iconsMap[resource.platform]}
          </div>
        )}
        {resource.link && (
          <Link
            href={resource.link.link || ""}
            className="text-primary-300 underline text-xs lg:text-base"
          >
            {resource.link.text}
          </Link>
        )}
      </div>
    </Reveal>
  );
}

export function Resources({ data }: { data: ResourcesSectionType }) {
  const { heading, subheading, description, resources } = data;
  return (
    <section className=" max-w-lg sm:max-w-screen-xl xl:max-w-screen-2xl mx-auto my-10 px-8 sm:px-24 bg-white">
      <Reveal className="flex flex-col gap-2 mb-10">
        <h3 className="font-medium text-lg sm:text-xl lg:text-3xl">
          {heading}
        </h3>
        {subheading && (
          <h4 className="font-normal text-xs sm:text-base lg:text-xl max-w-3xl">
            {subheading}
          </h4>
        )}

        {description && (
          <PortableText
            value={description}
            components={{
              block: {
                normal: ({ children }) => (
                  <p className="font-normal text-xs sm:text-base lg:text-xl max-w-3xl">
                    {children}
                  </p>
                ),
              },
            }}
          />
        )}
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 justify-center items-center">
        {resources.map((resource, index) => (
          <Resource resource={resource} key={index} />
        ))}
      </div>
    </section>
  );
}
